import {useEffect, useState, useMemo, useRef, type MutableRefObject} from 'react';
import {Grid, MenuItem, FormHelperText, Box, FormControlLabel, Checkbox} from '@mui/material';
import {ExpansionPanel, RangeDatePicker, InputField} from '@/components/common';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import type {CompanyAnnual} from '@/services/accountService/companyAnnualService';
import {
    computeYearOfAssessment,
    parseDateFlexible,
    daysBetween,
    isWithin18Months,
    isStrictDdMmYyyy,
    normalizeToDdMmYyyy,
    DATE_FORMAT_HINT,
} from '@/utils/dataUtils';
import {useFsCreateCtx} from '@/audit/finance-statement/create-stepper/context';

const MSG_OVER_18 =
    'Please note that the first audit period is over 18 month, a qualified opinion will be raised, confirm such period?';
const MSG_OVER_12 =
    'Please note that the audit period is over 12 month, a qualified opinion will be raised, confirm such period?';

type Props = {
    annualId: number;
    onChange?: (patch: Partial<CompanyAnnual>) => void;
    onValidationChange?: (isValid: boolean) => void;
    disableAccordion?: boolean;
    inCorpDate?: string | null;
    /** Parent calls on Next/Save; returns false and shows checkbox error when confirm required but unchecked */
    confirmValidateRef?: MutableRefObject<(() => boolean) | null>;
    externalErrors?: {
        periodFrom?: boolean;
        periodTo?: boolean;
        lastYearPeriodFrom?: boolean;
        lastYearPeriodTo?: boolean;
    };
    initialPeriodData?: {
        periodFrom?: string | null;
        periodTo?: string | null;
        lastYearPeriodFrom?: string | null;
        lastYearPeriodTo?: string | null;
        cessationDuringThisPeriod?: number;
        isMultiYearAudit?: boolean | null;
        firstAuditOver18Confirmed?: number | null;
    };
};

export default function EpPeriod({
                                     annualId,
                                     onChange,
                                     onValidationChange,
                                     disableAccordion = false,
                                     inCorpDate: inCorpDateProp,
                                     confirmValidateRef,
                                     externalErrors,
                                     initialPeriodData
                                 }: Props) {
    const getPeriodInfo = useCompanyAnnualStore(s => s.getPeriodInfo);
    const storeAnnual = useCompanyAnnualStore(s => s.byAnnualId[Number(annualId)]);

    // 从 Context 获取 inCorpDate / Next 门禁，在组件顶层调用 hook
    let fsCreateCtx: ReturnType<typeof useFsCreateCtx> | null = null;
    try {
        fsCreateCtx = useFsCreateCtx();
    } catch {
        // 如果不在 FsCreateStepper 中使用，Context 不存在
    }
    
    // Use prop if provided, otherwise get from context
    const inCorpDate = useMemo(() => {
        if (inCorpDateProp !== undefined) return inCorpDateProp;
        return (fsCreateCtx?.state.selectedCompany as any)?.inCorpDate || null;
    }, [inCorpDateProp, fsCreateCtx?.state.selectedCompany]);
    
    const [local, setLocal] = useState<{
        periodFrom: string | null;
        periodTo: string | null;
        lastYearPeriodFrom: string | null;
        lastYearPeriodTo: string | null;
        yearOfAssessment: string | null;
        auditMonths?: number;
        cessationDuringThisPeriod?: number;
        isMultiYearAudit?: boolean | null;
        firstAuditOver18Confirmed?: number | null;
    }>({
        periodFrom: null,
        periodTo: null,
        lastYearPeriodFrom: null,
        lastYearPeriodTo: null,
        yearOfAssessment: null,
        auditMonths: undefined,
        cessationDuringThisPeriod: 0, // Default to No
        isMultiYearAudit: true,
        firstAuditOver18Confirmed: 0,
    });

    const [fieldErrors, setFieldErrors] = useState<{
        periodFrom?: string;
        periodTo?: string;
        lastYearPeriodFrom?: string;
        lastYearPeriodTo?: string;
    }>({});
    const [confirmError, setConfirmError] = useState(false);

    // Track if initial data has been applied
    const initialDataAppliedRef = useRef(false);

    const initialPeriodKey = useMemo(() => {
        if (!initialPeriodData) return '';
        return [
            initialPeriodData.periodFrom,
            initialPeriodData.periodTo,
            initialPeriodData.lastYearPeriodFrom,
            initialPeriodData.lastYearPeriodTo,
            initialPeriodData.cessationDuringThisPeriod,
            initialPeriodData.isMultiYearAudit,
            initialPeriodData.firstAuditOver18Confirmed,
        ].join('|');
    }, [initialPeriodData]);

    useEffect(() => {
        initialDataAppliedRef.current = false;
    }, [annualId]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            if (!annualId) {
                // For new annual (annualId=0), apply initialPeriodData if provided
                if (initialPeriodData && !initialDataAppliedRef.current) {
                    initialDataAppliedRef.current = true;
                    setLocal(prev => ({
                        ...prev,
                        periodFrom: initialPeriodData.periodFrom ?? prev.periodFrom,
                        periodTo: initialPeriodData.periodTo ?? prev.periodTo,
                        lastYearPeriodFrom: initialPeriodData.lastYearPeriodFrom ?? prev.lastYearPeriodFrom,
                        lastYearPeriodTo: initialPeriodData.lastYearPeriodTo ?? prev.lastYearPeriodTo,
                        cessationDuringThisPeriod: initialPeriodData.cessationDuringThisPeriod ?? prev.cessationDuringThisPeriod,
                        isMultiYearAudit: initialPeriodData.isMultiYearAudit ?? prev.isMultiYearAudit ?? true,
                        firstAuditOver18Confirmed: initialPeriodData.firstAuditOver18Confirmed ?? prev.firstAuditOver18Confirmed ?? 0,
                    }));
                }
                return;
            }
            const v = await getPeriodInfo(annualId);
            if (!mounted) return;
            if (initialDataAppliedRef.current) return;
            initialDataAppliedRef.current = true;
            // Merge with initialPeriodData (initialPeriodData takes precedence for Excel import)
            if (initialPeriodData) {
                setLocal({
                    ...v,
                    periodFrom: initialPeriodData.periodFrom ?? v.periodFrom,
                    periodTo: initialPeriodData.periodTo ?? v.periodTo,
                    lastYearPeriodFrom: initialPeriodData.lastYearPeriodFrom ?? v.lastYearPeriodFrom,
                    lastYearPeriodTo: initialPeriodData.lastYearPeriodTo ?? v.lastYearPeriodTo,
                    cessationDuringThisPeriod: initialPeriodData.cessationDuringThisPeriod ?? v.cessationDuringThisPeriod,
                    isMultiYearAudit: initialPeriodData.isMultiYearAudit ?? v.isMultiYearAudit ?? true,
                    firstAuditOver18Confirmed: initialPeriodData.firstAuditOver18Confirmed ?? v.firstAuditOver18Confirmed ?? 0,
                });
            } else {
                setLocal({
                    ...v,
                    isMultiYearAudit: v.isMultiYearAudit ?? true,
                    firstAuditOver18Confirmed: v.firstAuditOver18Confirmed ?? 0,
                });
            }
        })();
        return () => {
            mounted = false;
        };
    }, [annualId, getPeriodInfo, initialPeriodKey, initialPeriodData]);

    // Reactively sync period fields when store data changes (e.g. from Content Sheet import)
    useEffect(() => {
        if (!storeAnnual) return;
        setLocal(prev => {
            const next = {
                periodFrom: storeAnnual.periodFrom ?? prev.periodFrom,
                periodTo: storeAnnual.periodTo ?? prev.periodTo,
                lastYearPeriodFrom: storeAnnual.lastYearPeriodFrom ?? prev.lastYearPeriodFrom,
                lastYearPeriodTo: storeAnnual.lastYearPeriodTo ?? prev.lastYearPeriodTo,
                yearOfAssessment: storeAnnual.yearOfAssessment ?? prev.yearOfAssessment,
                auditMonths: storeAnnual.auditMonths ?? prev.auditMonths,
                cessationDuringThisPeriod: storeAnnual.cessationDuringThisPeriod ?? prev.cessationDuringThisPeriod,
                isMultiYearAudit: storeAnnual.isMultiYearAudit ?? prev.isMultiYearAudit ?? true,
                firstAuditOver18Confirmed: storeAnnual.firstAuditOver18Confirmed ?? prev.firstAuditOver18Confirmed ?? 0,
            };
            // Only update if something actually changed
            if (
                next.periodFrom === prev.periodFrom &&
                next.periodTo === prev.periodTo &&
                next.lastYearPeriodFrom === prev.lastYearPeriodFrom &&
                next.lastYearPeriodTo === prev.lastYearPeriodTo &&
                next.yearOfAssessment === prev.yearOfAssessment &&
                next.auditMonths === prev.auditMonths &&
                next.cessationDuringThisPeriod === prev.cessationDuringThisPeriod &&
                next.isMultiYearAudit === prev.isMultiYearAudit &&
                next.firstAuditOver18Confirmed === prev.firstAuditOver18Confirmed
            ) {
                return prev;
            }
            return next;
        });
    }, [storeAnnual]);

    // 检查 inCorpDate 和 periodFrom 是否相等
    const shouldHideLastYearPeriod = useMemo(() => {
        if (!inCorpDate || !local.periodFrom) return false;
        return inCorpDate === local.periodFrom;
    }, [inCorpDate, local.periodFrom]);

    // 1.1.1: 当 periodFrom 与 corpDate 相同时，清空 lastYearPeriodFrom 和 lastYearPeriodTo
    useEffect(() => {
        if (shouldHideLastYearPeriod && (local.lastYearPeriodFrom || local.lastYearPeriodTo)) {
            setLocal(prev => ({
                ...prev,
                lastYearPeriodFrom: null,
                lastYearPeriodTo: null,
            }));
            onChangeRef.current?.({
                lastYearPeriodFrom: null,
                lastYearPeriodTo: null,
            });
        }
    }, [shouldHideLastYearPeriod, local.lastYearPeriodFrom, local.lastYearPeriodTo]);

    // Use ref to store onChange callback to avoid recreating emit function
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    const emit = (patch: Partial<typeof local>) => {
        setLocal(prev => ({...prev, ...patch}));
        onChangeRef.current?.(patch as Partial<CompanyAnnual>);

        // Clear field errors when user makes changes
        if (patch.periodFrom !== undefined) {
            setFieldErrors(prev => ({...prev, periodFrom: undefined}));
        }
        if (patch.periodTo !== undefined) {
            setFieldErrors(prev => ({...prev, periodTo: undefined}));
        }
        if (patch.lastYearPeriodFrom !== undefined) {
            setFieldErrors(prev => ({...prev, lastYearPeriodFrom: undefined}));
        }
        if (patch.lastYearPeriodTo !== undefined) {
            setFieldErrors(prev => ({...prev, lastYearPeriodTo: undefined}));
        }
    };

    // Validate required fields + dd/MM/yyyy format
    const validateFields = (): boolean => {
        const errors: typeof fieldErrors = {};

        if (!local.periodFrom) {
            errors.periodFrom = 'Period From is required';
        } else if (!isStrictDdMmYyyy(local.periodFrom)) {
            const fixed = normalizeToDdMmYyyy(local.periodFrom);
            if (fixed) {
                emit({periodFrom: fixed});
            } else {
                errors.periodFrom = DATE_FORMAT_HINT;
            }
        } else if (inCorpDate && local.periodFrom) {
            // Check if periodFrom is before inCorpDate
            const periodFromDate = parseDMY(local.periodFrom);
            const inCorpDateObj = parseDMY(normalizeToDdMmYyyy(inCorpDate) || inCorpDate);
            if (periodFromDate && inCorpDateObj && periodFromDate < inCorpDateObj) {
                errors.periodFrom = 'Period From cannot be before Incorporation Date';
            }
        }
        if (!local.periodTo) {
            errors.periodTo = 'Period To is required';
        } else if (!isStrictDdMmYyyy(local.periodTo)) {
            const fixed = normalizeToDdMmYyyy(local.periodTo);
            if (fixed) {
                emit({periodTo: fixed});
            } else {
                errors.periodTo = DATE_FORMAT_HINT;
            }
        }
        if (!shouldHideLastYearPeriod) {
            if (!local.lastYearPeriodFrom) {
                errors.lastYearPeriodFrom = 'Last Year Period From is required';
            } else if (!isStrictDdMmYyyy(local.lastYearPeriodFrom)) {
                const fixed = normalizeToDdMmYyyy(local.lastYearPeriodFrom);
                if (fixed) {
                    emit({lastYearPeriodFrom: fixed});
                } else {
                    errors.lastYearPeriodFrom = DATE_FORMAT_HINT;
                }
            }
            if (!local.lastYearPeriodTo) {
                errors.lastYearPeriodTo = 'Last Year Period To is required';
            } else if (!isStrictDdMmYyyy(local.lastYearPeriodTo)) {
                const fixed = normalizeToDdMmYyyy(local.lastYearPeriodTo);
                if (fixed) {
                    emit({lastYearPeriodTo: fixed});
                } else {
                    errors.lastYearPeriodTo = DATE_FORMAT_HINT;
                }
            }
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Helper function to parse DD/MM/YYYY date
    const parseDMY = (dateStr: string): Date | null => {
        const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateStr);
        if (!match) return null;
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1;
        const year = parseInt(match[3], 10);
        return new Date(year, month, day);
    };

    // Helper function to calculate months between two dates
    const monthsBetween = (from: Date, to: Date): number => {
        const yearDiff = to.getFullYear() - from.getFullYear();
        const monthDiff = to.getMonth() - from.getMonth();
        const dayDiff = to.getDate() - from.getDate();
        let months = yearDiff * 12 + monthDiff;
        // If the day of 'to' is less than 'from', subtract one month
        if (dayDiff < 0) {
            months--;
        }
        return months;
    };

    // Determine if period from date is within 18 months from inCorpDate
    const isInCorpWithin18Months = useMemo(() => {
        if (!inCorpDate || !local.periodFrom) return false;
        const incorpDateObj = parseDateFlexible(inCorpDate);
        const periodFromObj = parseDateFlexible(local.periodFrom);
        if (!incorpDateObj || !periodFromObj) return false;
        const months = monthsBetween(incorpDateObj, periodFromObj);
        return months <= 18;
    }, [inCorpDate, local.periodFrom]);

    // Calculate days between periodFrom and periodTo (inclusive)
    const periodDays = useMemo(() => {
        if (!local.periodFrom || !local.periodTo) return null;
        return daysBetween(local.periodFrom, local.periodTo);
    }, [local.periodFrom, local.periodTo]);

    // Determine if the period contains a leap year February (Feb 29)
    // For periods like 01/04/2023 ~ 31/03/2024, we need to check if Feb 2024 is in the period
    const isLeapYear = useMemo(() => {
        if (!local.periodFrom || !local.periodTo) return false;
        const fromDate = parseDateFlexible(local.periodFrom);
        const toDate = parseDateFlexible(local.periodTo);
        if (!fromDate || !toDate) return false;

        // Check all years that the period spans
        const startYear = fromDate.getFullYear();
        const endYear = toDate.getFullYear();

        for (let year = startYear; year <= endYear; year++) {
            // Check if this year is a leap year
            const isYearLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
            if (!isYearLeap) continue;

            // Check if Feb 29 of this year falls within the period
            const feb29 = new Date(year, 1, 29); // Month is 0-indexed, so 1 = February

            // Compare dates
            const fromTime = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate()).getTime();
            const toTime = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate()).getTime();
            const feb29Time = feb29.getTime();

            if (feb29Time >= fromTime && feb29Time <= toTime) {
                return true; // Period contains Feb 29 of a leap year
            }
        }

        return false;
    }, [local.periodFrom, local.periodTo]);

    // Get the number of days in a year (365 or 366)
    const daysInYear = useMemo(() => {
        return isLeapYear ? 366 : 365;
    }, [isLeapYear]);

    const isFirstAuditPeriod = useMemo(() => {
        if (!inCorpDate || !local.periodFrom) return false;
        return inCorpDate === local.periodFrom;
    }, [inCorpDate, local.periodFrom]);

    const isCessation = local.cessationDuringThisPeriod === 1;

    /** Step 1 only (not step 2 dual condition); show when > 12 months */
    const showMultiYearCheckbox = useMemo(() => {
        if (isFirstAuditPeriod && isCessation) return false;
        if (!isFirstAuditPeriod && !isCessation) return false;
        if (periodDays == null) return false;
        return periodDays > daysInYear;
    }, [isFirstAuditPeriod, isCessation, periodDays, daysInYear]);

    /**
     * Qualified-opinion confirm checkbox (merged):
     * - first audit + period > 18 months → 18m message
     * - non-first + period > 12 months (days) → 12m message
     */
    const periodConfirm = useMemo(() => {
        if (!local.periodFrom || !local.periodTo) return null;
        if (isFirstAuditPeriod) {
            if (isWithin18Months(local.periodFrom, local.periodTo)) return null;
            return {message: MSG_OVER_18};
        }
        if (periodDays == null || periodDays <= daysInYear) return null;
        return {message: MSG_OVER_12};
    }, [isFirstAuditPeriod, local.periodFrom, local.periodTo, periodDays, daysInYear]);

    const showPeriodConfirm = !!periodConfirm;

    // Hide confirm → reset to 0 and persist via onChange
    useEffect(() => {
        if (showPeriodConfirm) return;
        setConfirmError(false);
        if ((local.firstAuditOver18Confirmed ?? 0) === 0) return;
        setLocal(prev => ({...prev, firstAuditOver18Confirmed: 0}));
        onChangeRef.current?.({firstAuditOver18Confirmed: 0});
    }, [showPeriodConfirm, local.firstAuditOver18Confirmed]);

    // Parent Next/Save: show checkbox error when confirm required but unchecked
    useEffect(() => {
        if (!confirmValidateRef) return;
        confirmValidateRef.current = () => {
            if (!periodConfirm) {
                setConfirmError(false);
                return true;
            }
            if ((local.firstAuditOver18Confirmed ?? 0) === 1) {
                setConfirmError(false);
                return true;
            }
            setConfirmError(true);
            return false;
        };
        return () => {
            confirmValidateRef.current = null;
        };
    }, [confirmValidateRef, periodConfirm, local.firstAuditOver18Confirmed]);

    // Notify parent of validation status
    useEffect(() => {
        // 如果 inCorpDate 和 periodFrom 相等，跳过 lastYearPeriod 的校验
        let isValid: boolean;
        if (shouldHideLastYearPeriod) {
            isValid = !!(local.periodFrom && local.periodTo);
        } else {
            isValid = !!(local.periodFrom && local.periodTo && local.lastYearPeriodFrom && local.lastYearPeriodTo);
        }

        // Also check if periodFrom is not before inCorpDate
        if (isValid && inCorpDate && local.periodFrom) {
            const periodFromDate = parseDMY(local.periodFrom);
            const inCorpDateObj = parseDMY(inCorpDate);
            if (periodFromDate && inCorpDateObj && periodFromDate < inCorpDateObj) {
                isValid = false;
            }
        }

        onValidationChange?.(isValid);
    }, [local.periodFrom, local.periodTo, local.lastYearPeriodFrom, local.lastYearPeriodTo, inCorpDate, onValidationChange, shouldHideLastYearPeriod]);

    // Auto-compute Year of Assessment when periodTo changes
    useEffect(() => {
        if (!local.periodTo) return;
        const yoa = computeYearOfAssessment(local.periodTo);
        // Only update if changed to avoid loops
        if ((local.yearOfAssessment || null) !== (yoa || null)) {
            setLocal(prev => ({...prev, yearOfAssessment: yoa}));
            onChangeRef.current?.({yearOfAssessment: yoa});
        }
    }, [local.periodTo, local.yearOfAssessment]);

    // Auto-fill lastYearPeriod when period is standard financial year format (01/04/yyyy ~ 31/03/yyyy+1)
    useEffect(() => {
        // Only auto-fill if lastYearPeriod is empty and period is set
        if (local.lastYearPeriodFrom || local.lastYearPeriodTo) return;
        if (!local.periodFrom || !local.periodTo) return;

        const fromDate = parseDMY(local.periodFrom);
        const toDate = parseDMY(local.periodTo);
        if (!fromDate || !toDate) return;

        // Check if period is in standard format: 01/04/yyyy ~ 31/03/yyyy+1
        const isStandardFormat =
            fromDate.getDate() === 1 &&
            fromDate.getMonth() === 3 && // April (0-indexed)
            toDate.getDate() === 31 &&
            toDate.getMonth() === 2 && // March (0-indexed)
            toDate.getFullYear() === fromDate.getFullYear() + 1;

        if (isStandardFormat) {
            // Auto-fill with previous year: 01/04/yyyy-1 ~ 31/03/yyyy
            const lastYearFrom = new Date(fromDate.getFullYear() - 1, 3, 1); // April 1, yyyy-1
            const lastYearTo = new Date(fromDate.getFullYear(), 2, 31); // March 31, yyyy

            const formatDate = (date: Date): string => {
                const d = String(date.getDate()).padStart(2, '0');
                const m = String(date.getMonth() + 1).padStart(2, '0');
                const y = date.getFullYear();
                return `${d}/${m}/${y}`;
            };

            const lastYearFromStr = formatDate(lastYearFrom);
            const lastYearToStr = formatDate(lastYearTo);

            setLocal(prev => ({
                ...prev,
                lastYearPeriodFrom: lastYearFromStr,
                lastYearPeriodTo: lastYearToStr,
            }));
            onChangeRef.current?.({
                lastYearPeriodFrom: lastYearFromStr,
                lastYearPeriodTo: lastYearToStr,
            });
        }
    }, [local.periodFrom, local.periodTo, local.lastYearPeriodFrom, local.lastYearPeriodTo]);

    // Auto-compute Reporting Period based on inCorpDate and period dates
    useEffect(() => {
        if (!local.periodFrom || !local.periodTo || periodDays === null) return;

        let newValue: number | undefined = undefined;

        if (isInCorpWithin18Months) {
            // Case 1.2: inCorpDate is within 18 months from now
            // Use days calculation: 18 months ≈ 547 days (365 + 182) or 548 days (366 + 182) for leap year
            const maxDaysFor18Months = isLeapYear ? 548 : 547;
            if (periodDays > maxDaysFor18Months) {
                newValue = 1; // over 18 months
            } else {
                newValue = 0; // within 18 months (default)
            }
        } else {
            // Case 1.3: inCorpDate is over 18 months from now
            // Compare with 365 (平年) or 366 (润年) days
            if (periodDays > daysInYear) {
                newValue = 4; // more than 12 months
            } else if (periodDays === daysInYear) {
                newValue = 3; // 12 months (default)
            } else {
                newValue = 2; // less than 12 months
            }
        }

        if ((local.auditMonths ?? -1) !== newValue) {
            setLocal(prev => ({...prev, auditMonths: newValue}));
            onChangeRef.current?.({auditMonths: newValue});
        }
    }, [local.periodFrom, local.periodTo, local.auditMonths, isInCorpWithin18Months, periodDays, daysInYear, isLeapYear]);

    return (
        <ExpansionPanel
            title="Period (期間)"
            defaultExpanded
            expanded={disableAccordion ? true : undefined}
            onChange={disableAccordion ? () => {
            } : undefined}
        >
            <Grid container spacing={0.5}>
                <Grid size={6 as any}>
                    <Box>
                        <RangeDatePicker
                            variant="outlined"
                            label="Period (期間)"
                            from={local.periodFrom}
                            to={local.periodTo}
                            onChangeFrom={(from) => emit({periodFrom: from || null})}
                            onChangeTo={(to) => emit({periodTo: to || null})}
                            minDate={inCorpDate || undefined}
                            required
                            error={externalErrors?.periodFrom || externalErrors?.periodTo || !!fieldErrors.periodTo}
                        />
                        {(fieldErrors.periodFrom || fieldErrors.periodTo || externalErrors?.periodFrom || externalErrors?.periodTo) && (
                            <FormHelperText error sx={{ml: 2}}>
                                {fieldErrors.periodFrom || fieldErrors.periodTo || (externalErrors?.periodFrom && 'Period From is required') || (externalErrors?.periodTo && 'Period To is required')}
                            </FormHelperText>
                        )}
                    </Box>
                </Grid>
                {!shouldHideLastYearPeriod && (
                    <Grid size={6 as any}>
                        <Box>
                            <RangeDatePicker
                                variant="outlined"
                                label="Last Year Period (上一年期間)"
                                from={local.lastYearPeriodFrom}
                                to={local.lastYearPeriodTo}
                                onChangeFrom={(from) => emit({lastYearPeriodFrom: from || null})}
                                onChangeTo={(to) => emit({lastYearPeriodTo: to || null})}
                                minDate={inCorpDate || undefined}
                                required
                                error={externalErrors?.lastYearPeriodFrom || externalErrors?.lastYearPeriodTo}
                            />
                            {(fieldErrors.lastYearPeriodFrom || fieldErrors.lastYearPeriodTo || externalErrors?.lastYearPeriodFrom || externalErrors?.lastYearPeriodTo) && (
                                <FormHelperText error sx={{ml: 2}}>
                                    {fieldErrors.lastYearPeriodFrom || fieldErrors.lastYearPeriodTo || (externalErrors?.lastYearPeriodFrom && 'Last Year Period From is required') || (externalErrors?.lastYearPeriodTo && 'Last Year Period To is required')}
                                </FormHelperText>
                            )}
                        </Box>
                    </Grid>
                )}
                <Grid size={6 as any}>
                    <InputField fullWidth variant="outlined" label="Year of assessment (評稅年度)"
                                value={local.yearOfAssessment || ''} display/>
                </Grid>
                <Grid size={6 as any}>
                    <InputField
                        select
                        fullWidth
                        variant="outlined"
                        align="left"
                        label="Reporting Period"
                        value={local.auditMonths === undefined || local.auditMonths === null ? '' : local.auditMonths}
                        onChange={(e) => emit({auditMonths: e.target.value === '' ? undefined : Number(e.target.value) as any})}
                        display
                    >
                        <MenuItem dense value="">Select</MenuItem>
                        <MenuItem dense value={0} sx={{display: isInCorpWithin18Months ? 'block' : 'none'}}>within 18
                            months</MenuItem>
                        <MenuItem dense value={1} sx={{display: isInCorpWithin18Months ? 'block' : 'none'}}>over 18
                            months</MenuItem>
                        <MenuItem dense value={2} sx={{display: !isInCorpWithin18Months ? 'block' : 'none'}}>less than
                            12 months</MenuItem>
                        <MenuItem dense value={3} sx={{display: !isInCorpWithin18Months ? 'block' : 'none'}}>12
                            months</MenuItem>
                        <MenuItem dense value={4} sx={{display: !isInCorpWithin18Months ? 'block' : 'none'}}>more than
                            12 months</MenuItem>
                    </InputField>
                </Grid>
                <Grid size={6 as any}>
                    <InputField select fullWidth variant="outlined" align="left"
                                label="Cessation during this period? (此期間是否有停止營業)"
                                value={local.cessationDuringThisPeriod === undefined || local.cessationDuringThisPeriod === null ? '' : local.cessationDuringThisPeriod}
                                onChange={(e) => emit({cessationDuringThisPeriod: e.target.value === '' ? undefined : Number(e.target.value) as any})}>
                        <MenuItem dense value="">Select</MenuItem>
                        <MenuItem dense value={0}>No</MenuItem>
                        <MenuItem dense value={1}>Yes</MenuItem>
                    </InputField>
                </Grid>
                {showMultiYearCheckbox && (
                    <Grid size={12 as any} sx={{display: 'flex', alignItems: 'center', pl: 1}}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={local.isMultiYearAudit !== false}
                                    onChange={(e) => emit({isMultiYearAudit: e.target.checked})}
                                    size="small"
                                />
                            }
                            label="Is it a multi-year audit?"
                        />
                    </Grid>
                )}
                {showPeriodConfirm && periodConfirm && (
                    <Grid size={12 as any} sx={{pl: 1}}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={(local.firstAuditOver18Confirmed ?? 0) === 1}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        emit({firstAuditOver18Confirmed: checked ? 1 : 0});
                                        if (checked) setConfirmError(false);
                                    }}
                                    size="small"
                                    color={confirmError ? 'error' : 'primary'}
                                />
                            }
                            label={periodConfirm.message}
                        />
                        {confirmError && (
                            <FormHelperText error sx={{ml: 4, mt: 0}}>
                                Please confirm such period
                            </FormHelperText>
                        )}
                    </Grid>
                )}
            </Grid>
        </ExpansionPanel>
    );
}
